import {
  APIFieldAttributes,
  ArdoqId,
  TimelinesGridPrecisionUnit,
  UnitOfTime,
} from '@ardoq/api-types';
import { DateRangeFieldAttributes, MIN_DATE } from '@ardoq/date-range';
import {
  BuildItemsReducerState,
  ItemPhasesRow,
  Milestone,
  OccupiedTimespan,
  OtherLabelsRenderingData,
  RelevantCompontsScopeDataMap,
  TimelineItemModel,
  TimelinePhase,
  TimelineViewSettings,
  ViewRelevantComponentScopeData,
  WorkspaceAndFieldsRegistry,
} from '../types';
import {
  ChartDimensions,
  GraphGroup,
  GraphItem,
  TimeScalingFunction,
} from '@ardoq/graph';
import { isTruthy } from '@ardoq/common-helpers';
import {
  areSortedPhasesOverlapping,
  findExistingFreeRowForMilestone,
  findExistingFreeRowForPhase,
  findMinAndMaxForAllDates,
  getOccupiedStartAndEndForPhase,
  getOccupiedTimespanForMilestone,
  replaceDomainBounds,
  toMilestone,
  toPhase,
} from './calculations';
import {
  getNumberOfUnitsToPreviousTick,
  getTopLabelRowGridPrecision,
} from '../xAxis/calculations';
import { getLabelTicksForVisibleChartDomain } from '../grid/ticks';
import {
  getNoXAxisTicksLabel,
  getTimeAxisRowSegmentLabelInfo,
} from '../xAxis/utils';
import {
  GRID_LABELS_BOTTOM_ROW_HEIGHT,
  GRID_LABELS_TOP_ROW_HEIGHT,
} from '../xAxis/consts';
import { logError } from '@ardoq/logging';

const updateFieldBounds = (
  fieldStart: Date,
  fieldEnd: Date,
  fieldBounds: OccupiedTimespan
) => {
  if (!fieldBounds.occupiedStart || fieldStart < fieldBounds.occupiedStart) {
    fieldBounds.occupiedStart = fieldStart;
  }
  if (!fieldBounds.occupiedEnd || fieldEnd > fieldBounds.occupiedEnd) {
    fieldBounds.occupiedEnd = fieldEnd;
  }
};

const addPhaseToExistingRow = (row: ItemPhasesRow, phase: TimelinePhase) => {
  row.phases.push(phase);

  const { occupiedStart, occupiedEnd } = getOccupiedStartAndEndForPhase(phase);

  updateFieldBounds(occupiedStart, occupiedEnd, row.phasesBounds);
};
const addMilestoneToExistingRow = (
  row: ItemPhasesRow,
  milestone: Milestone
) => {
  row.milestones.push(milestone);

  const { occupiedStart, occupiedEnd } =
    getOccupiedTimespanForMilestone(milestone);

  updateFieldBounds(occupiedStart, occupiedEnd, row.milestonesBounds);
};

const getEmptyRow = (): ItemPhasesRow => ({
  phases: [],
  milestones: [],
  phasesBounds: { occupiedStart: null, occupiedEnd: null },
  milestonesBounds: { occupiedStart: null, occupiedEnd: null },
});

const reduceSortedPhasesToRows = (
  rows: ItemPhasesRow[],
  phase: TimelinePhase
): ItemPhasesRow[] => {
  const freeRow = findExistingFreeRowForPhase(rows, phase);

  if (freeRow) {
    addPhaseToExistingRow(freeRow, phase);
  } else {
    rows.push({
      ...getEmptyRow(),
      phases: [phase],
      phasesBounds: getOccupiedStartAndEndForPhase(phase),
    });
  }

  return rows;
};
const addMilestonesToRows = (
  rows: ItemPhasesRow[],
  milestone: Milestone
): ItemPhasesRow[] => {
  const freeRow = findExistingFreeRowForMilestone(rows, milestone);

  if (freeRow) {
    addMilestoneToExistingRow(freeRow, milestone);
  } else {
    rows.push({
      ...getEmptyRow(),
      milestones: [milestone],
      milestonesBounds: getOccupiedTimespanForMilestone(milestone),
    });
  }

  return rows;
};

type GetItemPhasesRowsArgs = {
  sortedDisplayedPhases: TimelinePhase[];
  milestones: Milestone[];
};
const getItemPhasesRows = ({
  sortedDisplayedPhases,
  milestones,
}: GetItemPhasesRowsArgs): ItemPhasesRow[] => {
  const phaseRows = sortedDisplayedPhases.reduce(reduceSortedPhasesToRows, [
    getEmptyRow(),
  ]);
  return milestones.reduce(addMilestonesToRows, phaseRows);
};

type GetSortedFieldsArgs = {
  componentData: ViewRelevantComponentScopeData | undefined;
  orderedSelectedDateRangeFields: DateRangeFieldAttributes[];
  orderedSelectedMilestoneFields: APIFieldAttributes[];
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
};
const getSortedFields = ({
  componentData,
  orderedSelectedDateRangeFields,
  orderedSelectedMilestoneFields,
  workspaceAndFieldsRegistry,
}: GetSortedFieldsArgs): {
  sortedDisplayedPhases: TimelinePhase[];
  sortedMilestones: Milestone[];
} => {
  const workspaceId = componentData?.workspaceId ?? null;
  const componentTypeId = componentData?.componentTypeId ?? null;

  if (!componentData || !workspaceId || !componentTypeId) {
    // it's a non-component group
    return {
      sortedDisplayedPhases: [],
      sortedMilestones: [],
    };
  }

  const componentTypeFields =
    workspaceAndFieldsRegistry
      .get(workspaceId)
      ?.byComponentType.get(componentTypeId) ?? [];

  const sortedDisplayedPhases: TimelinePhase[] = orderedSelectedDateRangeFields
    .filter(({ dateTimeFields: { start } }) =>
      componentTypeFields.some(({ _id }) => _id === start._id)
    )
    .map(timeSpanField =>
      toPhase(
        componentData,
        timeSpanField,
        orderedSelectedDateRangeFields.indexOf(timeSpanField)
      )
    )
    .filter(isTruthy)
    .sort(
      (phaseA, phaseB) =>
        (phaseA.start || MIN_DATE).getTime() -
        (phaseB.start || MIN_DATE).getTime()
    );

  const sortedMilestones: Milestone[] = componentData
    ? orderedSelectedMilestoneFields
        .filter(({ _id: milestoneId }) =>
          componentTypeFields.some(({ _id }) => _id === milestoneId)
        )
        .map(milestoneField =>
          toMilestone(
            componentData,
            milestoneField,
            orderedSelectedMilestoneFields.indexOf(milestoneField)
          )
        )
        .filter(isTruthy)
        .sort(
          (milestoneA, milestoneB) =>
            milestoneA.date.getTime() - milestoneB.date.getTime()
        )
    : [];

  return {
    sortedDisplayedPhases,
    sortedMilestones,
  };
};

type NodeToItemArgs = {
  node: GraphItem;
  treeDepth: number;
  orderedSelectedDateRangeFields: DateRangeFieldAttributes[];
  orderedSelectedMilestoneFields: APIFieldAttributes[];
  contextComponentId: ArdoqId;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
  rawItemIdPath: string;
  isGroup: boolean;
  isFieldDataCollapsingEnabled: boolean;
};
const nodeToItem = ({
  node,
  treeDepth,
  orderedSelectedDateRangeFields,
  orderedSelectedMilestoneFields,
  contextComponentId,
  viewRelevantComponentsScopeData,
  workspaceAndFieldsRegistry,
  rawItemIdPath,
  isGroup,
  isFieldDataCollapsingEnabled,
}: NodeToItemArgs): {
  item: TimelineItemModel;
  allItemDates: (Date | null)[];
} | null => {
  const componentId = node.modelId;

  const componentData = viewRelevantComponentsScopeData.get(componentId);

  if (!(node instanceof GraphGroup) && !componentData) {
    return null;
  }

  const itemLabels = node.getItemLabels();

  if (!itemLabels) {
    logError(
      new Error('Item labels are missing in GraphItem'),
      `Set Item labels on the GraphItem before building the view items,
       by either setting them in the constructor or by calling setItemLabels`
    );

    return null;
  }

  const { sortedDisplayedPhases, sortedMilestones } = getSortedFields({
    componentData,
    orderedSelectedDateRangeFields,
    orderedSelectedMilestoneFields,
    workspaceAndFieldsRegistry,
  });

  const allItemDates = sortedDisplayedPhases
    .flatMap(({ start, end }) => [start, end])
    .concat(sortedMilestones.map(({ date }) => date));

  // this check will determine whether or not the collapsed state can be set and
  // whether or not the expander is displayed in the UI
  const isFieldDataCollapsible =
    isFieldDataCollapsingEnabled &&
    (Boolean(itemLabels.otherLabels?.length) ||
      areSortedPhasesOverlapping(sortedDisplayedPhases));

  /**
   * For perf reasons, we create both expanded and collapsed phases rows,
   * and swith between them when expanding/collapsing the field data.
   */
  const collapsedItemPhasesRows = [
    {
      phases: sortedDisplayedPhases,
      milestones: sortedMilestones,
      phasesBounds: findMinAndMaxForAllDates(allItemDates),
      milestonesBounds: findMinAndMaxForAllDates(allItemDates),
    },
  ];
  const expandedItemPhasesRows = isFieldDataCollapsible
    ? getItemPhasesRows({
        sortedDisplayedPhases,
        milestones: sortedMilestones,
      })
    : null;

  return {
    item: {
      treeDepth,
      collapsedItemPhasesRows,
      expandedItemPhasesRows,
      isFieldDataCollapsible,
      id: node.id,
      modelId: componentId,
      itemClass: node.getCSS(),
      itemLabels,
      isGroup,
      isComponent: Boolean(componentData),
      groupClass: isGroup
        ? `${node.getCSS({
            useAsBackgroundStyle: true,
          })} ${!componentData ? 'nonComponent' : ''}`.trim()
        : '',
      isContext:
        Boolean(contextComponentId) && componentId === contextComponentId,
      excluded: Boolean(
        componentData && !componentData.isIncludedInContextByFilter
      ),
      rawItemIdPath,
    },
    allItemDates,
  };
};

export const buidItemsReducer =
  (treeDepth = 0, parentRawItemIdPath?: string) =>
  (
    state: BuildItemsReducerState,
    currentNode: GraphItem
  ): BuildItemsReducerState => {
    const {
      viewGraphHierarchy,
      orderedSelectedDateRangeFields,
      orderedSelectedMilestoneFields,
      contextComponentId,
      viewRelevantComponentsScopeData,
      isFieldDataCollapsingEnabled,
      workspaceAndFieldsRegistry,
    } = state.nodeToItemParameters;

    const currentHierarchyNode = viewGraphHierarchy.get(currentNode.id)!;

    if (!currentHierarchyNode) {
      return state;
    }

    const children = currentHierarchyNode.children || [];

    const currentRawItemId = currentHierarchyNode.rawGraphItem.id;

    const isGroup = Boolean(children.length);

    const rawItemIdPath =
      parentRawItemIdPath && currentRawItemId
        ? `${parentRawItemIdPath}_${currentRawItemId}`
        : currentRawItemId;

    const { item, allItemDates } =
      nodeToItem({
        node: currentNode,
        orderedSelectedDateRangeFields,
        orderedSelectedMilestoneFields,
        contextComponentId,
        treeDepth,
        viewRelevantComponentsScopeData,
        isGroup,
        rawItemIdPath,
        isFieldDataCollapsingEnabled,
        workspaceAndFieldsRegistry,
      }) ?? {};

    if (!item || !allItemDates) {
      return state;
    }

    if (isGroup) {
      state.collapsibleGroupIds.add(rawItemIdPath);
      if (treeDepth === 0) {
        state.rootCollapsibleGroupIds.add(rawItemIdPath);
      }
    }

    if (item.isFieldDataCollapsible) {
      state.collapsibleFieldDataIds.add(rawItemIdPath);
      if (treeDepth === 0) {
        state.rootCollapsibleFieldDataIds.add(rawItemIdPath);
      }
    }

    state.items.push(item);
    state.displayedComponentIds.push(item.modelId);

    state.chartDomain = replaceDomainBounds(allItemDates, state.chartDomain);

    return (
      children.reduce(buidItemsReducer(treeDepth + 1, rawItemIdPath), state) ??
      state
    );
  };

type GetLabelRowPropsArgs = {
  scaleX: TimeScalingFunction;
  visibleDomain: [Date, Date];
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  dimensions: ChartDimensions;
  numberOfUnitsForMultiUnitTick: number;
  isTopRow: boolean;
};

export const getLabelRowProps = ({
  scaleX,
  visibleDomain,
  adjustedGridPrecision,
  dimensions,
  numberOfUnitsForMultiUnitTick,
  isTopRow,
}: GetLabelRowPropsArgs) => {
  const isDailyGrid = adjustedGridPrecision === UnitOfTime.DAY;

  const showTopRow =
    isDailyGrid ||
    adjustedGridPrecision === UnitOfTime.WEEK ||
    adjustedGridPrecision === UnitOfTime.QUARTER;

  if (isTopRow && !showTopRow) {
    return null;
  }

  const gridPrecision =
    isTopRow && showTopRow
      ? getTopLabelRowGridPrecision(adjustedGridPrecision)
      : adjustedGridPrecision;

  const rowLabelTicks = getLabelTicksForVisibleChartDomain({
    scaleX,
    visibleDomain,
    dimensions,
    gridPrecision,
    numberOfUnitsForMultiUnitTick,
    isTopRow,
  });

  const numberOfUnitsToPreviousTick = getNumberOfUnitsToPreviousTick(
    gridPrecision,
    rowLabelTicks
  );

  const standardTickDistance =
    rowLabelTicks.length > 1
      ? rowLabelTicks[1].tickPosition - rowLabelTicks[0].tickPosition
      : null;

  const rightChartEdge = dimensions.margin.left + dimensions.chart.width;

  const isWeekLabelInDailyGrid = isTopRow && gridPrecision === UnitOfTime.WEEK;
  const lastIndex = rowLabelTicks.length - 1;

  return {
    noVisibleTicksRowLabel: rowLabelTicks?.length
      ? null
      : getNoXAxisTicksLabel({
          visibleDomain,
          gridPrecision,
          isWeekLabelInDailyGrid,
        }),
    rowHeight: isTopRow
      ? GRID_LABELS_TOP_ROW_HEIGHT
      : GRID_LABELS_BOTTOM_ROW_HEIGHT,
    dimensions,
    tickLabelsToDisplay:
      rowLabelTicks.map((tickLabelInfo, index) => {
        const isLastLabel = index === lastIndex;
        const isFirstLabel = index === 0;

        const tickDistance = isLastLabel
          ? rightChartEdge - tickLabelInfo.tickPosition
          : standardTickDistance || dimensions.chart.width;

        return getTimeAxisRowSegmentLabelInfo({
          tickLabelInfo,
          isWeekLabelInDailyGrid,
          gridPrecision,
          numberOfUnitsToPreviousTick,
          isFirstLabel,
          isLastLabel,
          tickDistance,
          dimensions,
        });
      }) || [],
  };
};

export const getPersistedCollapsedStateFromViewSettings = (
  viewSettings: TimelineViewSettings
) => {
  if (!viewSettings.persistedCollapsedState) {
    return {
      collapsedFieldDataIds: new Set<string>(),
      collapsedGroupIds: new Set<string>(),
    };
  }

  const { collapsedFieldDataIds, collapsedGroupIds } =
    viewSettings.persistedCollapsedState;

  return {
    collapsedFieldDataIds: new Set<string>(collapsedFieldDataIds),
    collapsedGroupIds: new Set<string>(collapsedGroupIds),
  };
};

export const getOtherLabelPopoverProps = (
  otherLabelsRenderingData: OtherLabelsRenderingData,
  otherLabelIndex: number,
  popoverX: number,
  popoverY: number
) => {
  const otherLabel = otherLabelsRenderingData?.labelsData[otherLabelIndex];

  return otherLabel?.isTruncated
    ? {
        popoverX,
        popoverY,
        title: otherLabel.labelData[0],
        label: otherLabel.labelData[1],
      }
    : undefined;
};
